import type { ElementType } from 'react';
import { generatePath } from 'react-router-dom';

import { durationOrUnlimited } from 'productConfigurations/durationUnits';

import { LicenseDetails } from 'api/licenses';
import { creationDate, expiryDateString, formatDate, isDateEarlierThanToday, isRuntimeFeature } from 'licenses/utils';
import { ROUTES } from 'routes/routes';

type LicenseRowDataMapResult = Array<{
    title: string;
    accessor: string | ((license: LicenseDetails) => string);
    clipboard?: (license: LicenseDetails) => string;
    hide?: (license: LicenseDetails) => boolean;
    showError?: (license: LicenseDetails) => boolean;
    route?: (license: LicenseDetails) => string;
    link?: {
        route: (license: LicenseDetails) => string;
        type: string;
        toolTip: string;
    };
    linkType?: string;
    component?: ElementType;
}>;

export function useLicenseInfoRowDataMap(): LicenseRowDataMapResult {
    return [
        {
            title: 'Product Configuration:',
            accessor: license =>
                license.configuration?.isAdhocConfiguration ? '- None -' : license.configuration.name,
        },
        {
            title: 'Part Number:',
            accessor: license => license.configuration.partNo,
        },
        {
            title: 'License Key:',
            accessor: 'license_key',
        },
        {
            title: 'License ID:',
            accessor: 'id',
            clipboard: _ => window.location.href,
        },
        {
            title: 'Serial Number:',
            accessor: 'serial_number',
            hide: license => !license.configuration.hasSerialNumber,
            link: {
                route: license =>
                    license.configuration.hasSerialNumber &&
                    `${generatePath(ROUTES.LICENSE_LIST)}?filter=${encodeURIComponent(license.serial_number)}`,
                type: '_self',
                toolTip: 'View all licenses with this serial number',
            },
        },
        {
            title: 'Comment:',
            accessor: license => license.comment,
        },
        {
            title: 'Additional License Data:',
            accessor: license => {
                if (license.configuration?.additionalLicenseData) {
                    const maxChars = 100;
                    const oneLineJson = JSON.stringify(JSON.parse(license.configuration.additionalLicenseData));
                    return oneLineJson.length > maxChars ? oneLineJson.substring(0, maxChars) + '...' : oneLineJson;
                }
                return '';
            },
            hide: license => !license.configuration?.product.allow_additional_license_data,
        },
    ];
}

export function useLicenseTermsRowDataMap(): LicenseRowDataMapResult {
    return [
        {
            title: 'Validity:',
            showError: license => license.isExpired,
            accessor: license =>
                `${durationOrUnlimited(license.configuration.validity, license.configuration.validityUnit)} ${expiryDateString(license, license.expiration_date)}`,
        },
        {
            title: 'Support Period:',
            showError: license => isDateEarlierThanToday(license.support_expiration_date),
            accessor: license =>
                `${durationOrUnlimited(license.configuration.supportValidity, license.configuration.supportValidityUnit)} ${expiryDateString(license, license.support_expiration_date)}`,
        },
        {
            title: 'Features:',
            accessor: license =>
                license.features
                    ?.filter(f => !isRuntimeFeature(f))
                    .sort((a, b) => a.featureType.localeCompare(b.featureType) || a.name.localeCompare(b.name))
                    .map(f => (f.featureType === 'ADK Feature' ? `ADK: ${f.name}` : f.name))
                    .join(', ') || '',
        },
        {
            title: 'Application Names',
            accessor: license => license.configuration?.runtimeFeatures?.map(f => f.name).join(', ') || '',
            hide: license => !license.configuration?.product.is_application,
        },
        {
            title: 'Max Version:',
            accessor: 'max_version',
            hide: license => !license.configuration?.product.has_max_version,
        },
        {
            title: 'Activations (used / allowed):',
            accessor: license =>
                `${license.activeLicenseFilesCount} / ${
                    license.maximumActivations ? license.maximumActivations : 'unlimited'
                }`,
        },
        {
            title: 'Max Number of Concurrent Users:',
            accessor: license => license.maxNumberOfConcurrentUsers?.toString() ?? 'unlimited',
            hide: license => !license.configuration?.product.use_no_of_concurrent_users,
        },
        {
            title: 'Can Sign Files:',
            accessor: license => (license?.configuration?.can_sign_files ? 'Yes' : 'No'),
            hide: license => !license.configuration?.product?.can_sign_files,
        },
    ];
}

export function useLicenseCustomerInfoRowDataMap(): LicenseRowDataMapResult {
    return [
        {
            title: 'Customer Name:',
            accessor: 'customerFullName',
            link: {
                route: license => license.user && generatePath(ROUTES.CUSTOMER_DETAIL, { id: license.user.toString() }),
                type: '_blank',
                toolTip: 'Open customer page in a new tab',
            },
        },
        {
            title: 'Email:',
            accessor: 'customerEmail',
        },
        {
            title: 'Company:',
            accessor: 'customerCompany',
        },
        {
            title: 'Owner ID:',
            accessor: license => license.ownerId,
            hide: license => !license.configuration?.product.use_owner_id,
        },
    ];
}

export function useLicenseOrderInfoRowDataMap(): LicenseRowDataMapResult {
    return [
        {
            title: 'Sales Order Number:',
            accessor: license => license.sales_order_number,
        },
        {
            title: 'Purchase Order Number:',
            accessor: license => license.purchase_order_number,
        },
    ];
}

function determineStatus(license: LicenseDetails): string {
    if (license.invalidated) return 'Invalidated';
    if (license.isExpired) return 'Expired';
    if (license.activeLicenseFilesCount > 0) return 'Active';
    return 'Issued';
}

export function useLicenseStatusInfoRowDataMap(): Array<{
    title: string;
    accessor: string | ((license: LicenseDetails) => string);
    clipboard?: (license: LicenseDetails) => string;
    hide?: (license: LicenseDetails) => boolean;
    showError?: (license: LicenseDetails) => boolean;
    route?: (license: LicenseDetails) => string;
    link?: {
        route: (license: LicenseDetails) => string;
        type: string;
        toolTip: string;
    };
    linkType?: string;
    component?: ElementType;
}> {
    return [
        {
            title: 'Status:',
            accessor: determineStatus,
        },
        {
            title: 'Created On:',
            accessor: license => creationDate(license),
        },
        {
            title: 'Activated On:',
            accessor: license => formatDate(license.activation_date),
        },
        {
            title: 'Renewed On:',
            accessor: license => formatDate(license.renewal_date),
        },
    ];
}
